/**
 * Text statistics utilities for writing analysis
 * Provides character counts, word counts, readability scores, and time estimates
 */

export interface TextStats {
  // Core counts
  characters: number
  charactersNoSpaces: number
  words: number
  sentences: number
  paragraphs: number
  pages: number // ~250 words per page

  // Readability and complexity
  avgWordLength: number
  avgSentenceLength: number
  avgWordsPerParagraph: number
  syllables: number
  fleschKincaid: number // Grade level
  gunningFog: number // Grade level
  fleschReadingEase: number // 0-100 scale

  // Time estimates (in minutes)
  readingTime: number // ~250 words/minute
  speakingTime: number // ~150 words/minute
}

/**
 * Strip markdown formatting and HTML tags for accurate text analysis
 */
function stripFormatting(text: string): string {
  let cleaned = text

  // Remove HTML tags (including annotation marks)
  cleaned = cleaned.replace(/<[^>]+>/g, '')

  // Remove markdown annotation markers
  cleaned = cleaned.replace(/==/g, '')
  cleaned = cleaned.replace(/~~/g, '')

  // Remove markdown links [text](url) -> text
  cleaned = cleaned.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')

  // Remove markdown images ![alt](url)
  cleaned = cleaned.replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')

  // Remove markdown emphasis/bold (non-greedy to handle nested cases)
  cleaned = cleaned.replace(/\*\*(.+?)\*\*/g, '$1')
  cleaned = cleaned.replace(/\*(.+?)\*/g, '$1')
  cleaned = cleaned.replace(/__(.+?)__/g, '$1')
  cleaned = cleaned.replace(/_(.+?)_/g, '$1')

  // Remove markdown headers
  cleaned = cleaned.replace(/^#{1,6}\s+/gm, '')

  // Remove markdown list markers
  cleaned = cleaned.replace(/^[\s]*[-*+]\s+/gm, '')
  cleaned = cleaned.replace(/^[\s]*\d+\.\s+/gm, '')

  // Remove markdown task list markers
  cleaned = cleaned.replace(/\[[ x]\]\s*/gi, '')

  // Remove markdown blockquotes
  cleaned = cleaned.replace(/^>\s*/gm, '')

  // Remove markdown code blocks and inline code
  cleaned = cleaned.replace(/```[\s\S]*?```/g, '')
  cleaned = cleaned.replace(/`[^`]+`/g, '')

  // Remove card UUIDs
  cleaned = cleaned.replace(/\[[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\]/gi, '')

  // Normalize whitespace
  cleaned = cleaned.replace(/\s+/g, ' ').trim()

  return cleaned
}

/**
 * Count syllables in a word using a simplified algorithm
 * Based on the vowel counting method with common adjustments
 */
function countSyllables(word: string): number {
  word = word.toLowerCase().trim()
  if (word.length === 0) return 0
  if (word.length <= 3) return 1

  // Remove trailing 'e' (silent e)
  word = word.replace(/e$/, '')

  // Count vowel groups
  const vowelGroups = word.match(/[aeiouy]+/g)
  let count = vowelGroups ? vowelGroups.length : 1

  // Adjust for common patterns
  // -le at end often adds syllable
  if (word.match(/le$/)) count++
  // -ed at end is usually silent
  if (word.match(/ed$/)) count = Math.max(1, count - 1)

  return Math.max(1, count)
}

/**
 * Count complex words (3+ syllables) for Gunning Fog index
 */
function countComplexWords(words: string[]): number {
  return words.filter(word => {
    // Exclude proper nouns (capitalized), compound words with hyphens
    if (word.match(/^[A-Z]/) || word.includes('-')) return false
    return countSyllables(word) >= 3
  }).length
}

/**
 * Calculate all text statistics from content
 */
export function calculateTextStats(content: string): TextStats {
  if (!content || content.trim() === '') {
    return {
      characters: 0,
      charactersNoSpaces: 0,
      words: 0,
      sentences: 0,
      paragraphs: 0,
      pages: 0,
      avgWordLength: 0,
      avgSentenceLength: 0,
      avgWordsPerParagraph: 0,
      syllables: 0,
      fleschKincaid: 0,
      gunningFog: 0,
      fleschReadingEase: 0,
      readingTime: 0,
      speakingTime: 0,
    }
  }

  const cleanedText = stripFormatting(content)

  // Core counts
  const characters = cleanedText.length
  const charactersNoSpaces = cleanedText.replace(/\s/g, '').length

  // Word count - split on whitespace, filter empty
  const words = cleanedText.split(/\s+/).filter(w => w.length > 0)
  const wordCount = words.length

  // Sentence count - split on sentence-ending punctuation
  // Handle abbreviations and decimals by looking for space or end after punctuation
  const sentences = cleanedText.split(/[.!?]+(?:\s|$)/).filter(s => s.trim().length > 0)
  const sentenceCount = Math.max(1, sentences.length)

  // Paragraph count - from original content (before stripping)
  // Paragraphs are separated by blank lines
  const paragraphs = content.split(/\n\s*\n/).filter(p => p.trim().length > 0)
  const paragraphCount = Math.max(1, paragraphs.length)

  // Pages (estimated at ~250 words per page)
  const pages = Math.max(0, Math.ceil(wordCount / 250))

  // Average word length
  const totalWordLength = words.reduce((sum, w) => sum + w.replace(/[^a-zA-Z]/g, '').length, 0)
  const avgWordLength = wordCount > 0 ? totalWordLength / wordCount : 0

  // Average sentence length
  const avgSentenceLength = wordCount / sentenceCount

  // Average words per paragraph
  const avgWordsPerParagraph = wordCount / paragraphCount

  // Syllable count
  const syllables = words.reduce((sum, word) => sum + countSyllables(word), 0)

  // Flesch-Kincaid Grade Level
  // 0.39 * (words/sentences) + 11.8 * (syllables/words) - 15.59
  const fleschKincaid = wordCount > 0
    ? Math.max(0, 0.39 * avgSentenceLength + 11.8 * (syllables / wordCount) - 15.59)
    : 0

  // Flesch Reading Ease
  // 206.835 - 1.015 * (words/sentences) - 84.6 * (syllables/words)
  const fleschReadingEase = wordCount > 0
    ? Math.min(100, Math.max(0, 206.835 - 1.015 * avgSentenceLength - 84.6 * (syllables / wordCount)))
    : 0

  // Gunning Fog Index
  // 0.4 * ((words/sentences) + 100 * (complex words/words))
  const complexWordCount = countComplexWords(words)
  const gunningFog = wordCount > 0
    ? Math.max(0, 0.4 * (avgSentenceLength + 100 * (complexWordCount / wordCount)))
    : 0

  // Time estimates
  const readingTime = wordCount / 250 // ~250 words/minute
  const speakingTime = wordCount / 150 // ~150 words/minute

  return {
    characters,
    charactersNoSpaces,
    words: wordCount,
    sentences: sentenceCount,
    paragraphs: paragraphCount,
    pages,
    avgWordLength: Math.round(avgWordLength * 10) / 10,
    avgSentenceLength: Math.round(avgSentenceLength * 10) / 10,
    avgWordsPerParagraph: Math.round(avgWordsPerParagraph * 10) / 10,
    syllables,
    fleschKincaid: Math.round(fleschKincaid * 10) / 10,
    gunningFog: Math.round(gunningFog * 10) / 10,
    fleschReadingEase: Math.round(fleschReadingEase),
    readingTime,
    speakingTime,
  }
}

/**
 * Format time in minutes to human-readable string
 */
export function formatTime(minutes: number): string {
  if (minutes < 1) {
    const seconds = Math.round(minutes * 60)
    return seconds <= 0 ? '< 1 sec' : `${seconds} sec`
  }
  if (minutes < 60) {
    const mins = Math.round(minutes)
    return `${mins} min`
  }
  const hours = Math.floor(minutes / 60)
  const mins = Math.round(minutes % 60)
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
}

/**
 * Get reading level description from grade level
 */
export function getReadingLevelDescription(gradeLevel: number): string {
  if (gradeLevel <= 5) return 'Elementary'
  if (gradeLevel <= 8) return 'Middle School'
  if (gradeLevel <= 12) return 'High School'
  if (gradeLevel <= 16) return 'College'
  return 'Graduate'
}

/**
 * Get Flesch Reading Ease description
 */
export function getFleschDescription(score: number): string {
  if (score >= 90) return 'Very Easy'
  if (score >= 80) return 'Easy'
  if (score >= 70) return 'Fairly Easy'
  if (score >= 60) return 'Standard'
  if (score >= 50) return 'Fairly Difficult'
  if (score >= 30) return 'Difficult'
  return 'Very Difficult'
}
